import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} FreeShippingBarRefs
 * @property {HTMLParagraphElement} message
 * @property {HTMLDivElement} progress
 */

class FreeShippingBar extends HTMLElement {
  /** @type {(() => void) | null} */
  #boundOnCartUpdate = null;

  connectedCallback() {
    this.#update();
    this.#boundOnCartUpdate = () => requestAnimationFrame(() => this.#update());
    document.addEventListener(ThemeEvents.cartUpdate, this.#boundOnCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#boundOnCartUpdate);
  }

  disconnectedCallback() {
    if (this.#boundOnCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#boundOnCartUpdate);
      document.removeEventListener(ThemeEvents.discountUpdate, this.#boundOnCartUpdate);
    }
  }

  static get observedAttributes() {
    return ['threshold', 'total-price'];
  }

  attributeChangedCallback() {
    this.#update();
  }

  #update() {
    const threshold = Number.parseFloat(this.getAttribute('threshold') || '0');
    const totalPrice = Number.parseFloat(this.getAttribute('total-price') || '0');
    const remaining = Math.max(0, threshold - totalPrice);
    const progress = threshold > 0 ? Math.min(100, (totalPrice / threshold) * 100) : 0;

    const message = this.querySelector('.free-shipping-bar__message');
    const progressBar = this.querySelector('.free-shipping-bar__progress');

    if (message instanceof HTMLElement) {
      const template =
        remaining <= 0 ? this.getAttribute('reached-message') : this.getAttribute('unreached-message');
      message.textContent = this.#formatMessage(template, remaining);
    }

    if (progressBar instanceof HTMLElement) {
      progressBar.style.setProperty('--progress', String(progress));
      progressBar.setAttribute('aria-valuenow', String(Math.round(progress)));
    }
  }

  /**
   * @param {string | null} template
   * @param {number} remainingCents
   * @returns {string}
   */
  #formatMessage(template, remainingCents) {
    if (!template) return '';

    const moneyFormat = this.getAttribute('money-format') || '{{amount}}';
    const currency = this.getAttribute('currency') || 'USD';
    const remainingFormatted = formatMoney(remainingCents, moneyFormat, currency);

    return template.replace(/\{\{\s*remaining_amount\s*\}\}/g, remainingFormatted);
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
